import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { costProfileCostsHistory } from "@planzone/dto";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveCostProfileCostsHistoryRequest(
  state: State,
  costProfile: Boundaries["retrieveCostProfileCostsHistory"]["request"],
): State {
  const index = state.findIndex(
    (req) => req.costProfile.uuid === costProfile.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          costProfile,
          request: setRequestStateToPending(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          costProfile,
          request: setRequestStateToPending(),
          history: costProfileCostsHistory,
        },
      ];
}

function handleRetrieveCostProfileCostsHistorySuccess(
  state: State,
  {
    costProfile,
    costProfileCostsHistory,
  }: Boundaries["retrieveCostProfileCostsHistory"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.costProfile.uuid === costProfile.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          costProfile,
          request: setRequestStateToSuccess(),
          history: costProfileCostsHistory,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          costProfile,
          request: setRequestStateToSuccess(),
          history: costProfileCostsHistory,
        },
      ];
}

function handleRetrieveCostProfileCostsHistoryFailure(
  state: State,
  costProfile: Boundaries["retrieveCostProfileCostsHistory"]["request"],
): State {
  const index = state.findIndex(
    (req) => req.costProfile.uuid === costProfile.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], costProfile, request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          costProfile,
          request: setRequestStateToFailure(),
          history: costProfileCostsHistory,
        },
      ];
}

function handleRemoveCost(
  state: State,
  cost: Boundaries["removeCost"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    history: req.history.filter(({ uuid }) => uuid !== cost.uuid),
  }));
}

function handleEditCostRequest(
  state: State,
  { cost, nextCost }: Boundaries["editCost"]["request"],
): State {
  return state.map((req) => ({
    ...req,
    history: req.history.map((c) => (c.uuid === cost.uuid ? nextCost : c)),
  }));
}

function handleEditCostFailure(
  state: State,
  { cost, nextCost }: Boundaries["editCost"]["request"],
): State {
  return state.map((req) => ({
    ...req,
    history: req.history.map((c) => (c.uuid === cost.uuid ? cost : c)),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveCostProfileCostsHistoryRequested]:
      handleRetrieveCostProfileCostsHistoryRequest,
    [events.retrieveCostProfileCostsHistorySucceeded]:
      handleRetrieveCostProfileCostsHistorySuccess,
    [events.retrieveCostProfileCostsHistoryFailed]:
      handleRetrieveCostProfileCostsHistoryFailure,
    [events.removeCostSucceeded]: handleRemoveCost,
    [events.editCostRequested]: handleEditCostRequest,
    [events.editCostFailed]: handleEditCostFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
